import { useState, useRef } from 'react'
import {
  Box,
  VStack,
  HStack,
  SimpleGrid,
  Text,
  Avatar,
  AvatarGroup,
  Badge,
  Button,
  IconButton,
  Input,
  Textarea,
  Divider,
  FormControl,
  FormLabel,
  Collapse,
  Checkbox,
  Link,
  Select,
  Spinner,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  useDisclosure,
} from '@chakra-ui/react'
import { Link as RouterLink } from 'react-router-dom'
import { DeleteIcon, ChevronDownIcon, ChevronUpIcon, EditIcon, AddIcon } from '@chakra-ui/icons'
import { formatMeetingDate } from '../utils/date'
import { glassCard, inputStyle, labelStyle } from '../styles'
import { apiFetch } from '../utils/apiFetch'

export interface Beer {
  beerId?: string
  name: string
  brewery?: string
  url?: string
  foodPairing?: string
}

export interface Participant {
  name: string
  memberNumber?: number
  memberId?: string
  isGuest: boolean
}

export interface Meeting {
  _id: string
  date: string
  time: string
  hosts: { name: string; memberNumber?: number }[]
  location: string
  participantCount: number
  participants: Participant[]
  beers: Beer[]
  theme?: string
  food?: string
  notes?: string
  isAnnualMeeting: boolean
}

interface MemberOption {
  _id: string
  name?: string
  email: string
}

interface BeerSuggestion {
  _id: string
  name: string
  brewery?: string
  url?: string
}

function BeerNameInput({ value, onChange, onSelect, suggestions, breweryHint }: {
  value: string
  onChange: (v: string) => void
  onSelect: (b: BeerSuggestion) => void
  suggestions: BeerSuggestion[]
  breweryHint: string
}) {
  const [open, setOpen] = useState(false)
  const q = value.toLowerCase()
  const filtered = q.length < 1 ? [] : suggestions.filter(b => {
    if (!b.name.toLowerCase().includes(q)) return false
    if (breweryHint.trim()) return b.brewery?.toLowerCase().includes(breweryHint.toLowerCase()) ?? false
    return true
  }).slice(0, 8)

  return (
    <Box position="relative">
      <Input placeholder="Beer name" value={value}
        onChange={e => { onChange(e.target.value); setOpen(true) }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        {...inputStyle}
      />
      {open && filtered.length > 0 && (
        <Box position="absolute" top="100%" left={0} right={0} zIndex={20}
          bg="gray.800" borderRadius="md" border="1px solid" borderColor="whiteAlpha.300"
          maxH="180px" overflowY="auto" boxShadow="xl" mt="1px">
          {filtered.map(beer => (
            <HStack key={beer._id} px={3} py={2} cursor="pointer" _hover={{ bg: 'whiteAlpha.100' }}
              onMouseDown={() => { onSelect(beer); setOpen(false) }} spacing={2}>
              <Text fontSize="sm" color="white" flex={1} noOfLines={1}>{beer.name}</Text>
              {beer.brewery && <Text fontSize="xs" color="whiteAlpha.400" flexShrink={0}>{beer.brewery}</Text>}
            </HStack>
          ))}
        </Box>
      )}
    </Box>
  )
}

function BreweryInput({ value, onChange, suggestions }: {
  value: string
  onChange: (v: string) => void
  suggestions: BeerSuggestion[]
}) {
  const [open, setOpen] = useState(false)
  const q = value.toLowerCase()
  const uniqueBreweries = Array.from(new Set(suggestions.filter(b => b.brewery).map(b => b.brewery!))).sort()
  const filtered = q.length < 1 ? [] : uniqueBreweries.filter(name => name.toLowerCase().includes(q)).slice(0, 8)

  return (
    <Box position="relative">
      <Input placeholder="Brewery" value={value}
        onChange={e => { onChange(e.target.value); setOpen(true) }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        {...inputStyle}
      />
      {open && filtered.length > 0 && (
        <Box position="absolute" top="100%" left={0} right={0} zIndex={20}
          bg="gray.800" borderRadius="md" border="1px solid" borderColor="whiteAlpha.300"
          maxH="180px" overflowY="auto" boxShadow="xl" mt="1px">
          {filtered.map(name => (
            <Box key={name} px={3} py={2} cursor="pointer" _hover={{ bg: 'whiteAlpha.100' }}
              onMouseDown={() => { onChange(name); setOpen(false) }}>
              <Text fontSize="sm" color="white">{name}</Text>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  )
}

interface EditForm {
  date: string
  time: string
  location: string
  hosts: { name: string; memberId?: string; isOther?: boolean }[]
  theme: string
  food: string
  notes: string
  beers: { beerId?: string; name: string; brewery: string; url: string; foodPairing: string }[]
  participants: { name: string; memberId?: string; isGuest: boolean; isOther?: boolean }[]
}

function toEditForm(meeting: Meeting): EditForm {
  return {
    date: meeting.date.slice(0, 10),
    time: meeting.time,
    location: meeting.location,
    hosts: meeting.hosts.length > 0 ? meeting.hosts.map(host => ({ name: host.name, isOther: true })) : [{ name: '', isOther: true }],
    theme: meeting.theme ?? '',
    food: meeting.food ?? '',
    notes: meeting.notes ?? '',
    beers: meeting.beers.map(beer => ({
      beerId: beer.beerId,
      name: beer.name,
      brewery: beer.brewery ?? '',
      url: beer.url ?? '',
      foodPairing: beer.foodPairing ?? '',
    })),
    participants: meeting.participants.map(participant => ({
      name: participant.name,
      memberId: participant.memberId,
      isGuest: participant.isGuest,
      isOther: !participant.memberId,
    })),
  }
}

interface MeetingCardProps {
  meeting: Meeting
  isAdmin: boolean
  currentUserId?: string
  isNext?: boolean
  scrollRef?: (el: HTMLDivElement | null) => void
  onUpdate?: (updated: Meeting) => void
  onDelete?: (id: string) => void
}

export default function MeetingCard({ meeting, isAdmin, currentUserId, isNext = false, scrollRef, onUpdate, onDelete }: MeetingCardProps) {
  const [isExpanded, setIsExpanded] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [editForm, setEditForm] = useState<EditForm | null>(null)
  const [saving, setSaving] = useState(false)
  const [attending, setAttending] = useState(false)
  const [members, setMembers] = useState<MemberOption[]>([])
  const [beerSuggestions, setBeerSuggestions] = useState<BeerSuggestion[]>([])
  const [participantFilter, setParticipantFilter] = useState('')
  const [pendingParticipantId, setPendingParticipantId] = useState('')
  const [pendingParticipantName, setPendingParticipantName] = useState('')

  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure()
  const cancelDeleteRef = useRef<HTMLButtonElement>(null)

  const { isOpen: isDietaryOpen, onOpen: onDietaryOpen, onClose: onDietaryClose } = useDisclosure()
  const [dietaryInfo, setDietaryInfo] = useState<{ name: string; dietary: string }[]>([])
  const [dietaryLoading, setDietaryLoading] = useState(false)

  const handleDietaryOpen = async () => {
    const linkedIds = new Set(meeting.participants.filter(participant => participant.memberId).map(participant => participant.memberId!))
    if (linkedIds.size === 0) { setDietaryInfo([]); onDietaryOpen(); return }
    setDietaryLoading(true)
    onDietaryOpen()
    const res = await apiFetch('/api/users')
    if (res.ok) {
      const users: { _id: string; name?: string; email: string; dietary?: string }[] = await res.json()
      setDietaryInfo(
        users
          .filter(user => linkedIds.has(String(user._id)) && user.dietary)
          .map(user => ({ name: user.name ?? user.email, dietary: user.dietary! }))
      )
    }
    setDietaryLoading(false)
  }

  const handleAttend = async () => {
    setAttending(true)
    const res = await apiFetch(`/api/meetings/${meeting._id}/attend`, { method: 'POST' })
    if (res.ok) {
      const updated = await res.json()
      onUpdate?.(updated)
    }
    setAttending(false)
  }

  const isAlreadyAttending = !!currentUserId && meeting.participants.some(participant => participant.memberId === currentUserId)

  const isPast = new Date(meeting.date) < new Date()
  const { display, dayOfWeek } = formatMeetingDate(meeting.date)

  const startEdit = async () => {
    setEditForm(toEditForm(meeting))
    setIsEditing(true)
    setIsExpanded(true)
    if (members.length === 0) {
      apiFetch('/api/users')
        .then(res => res.ok ? res.json() : [])
        .then((data: MemberOption[]) => setMembers(data.filter(m => m._id)))
    }
    if (beerSuggestions.length === 0) {
      apiFetch('/api/beers')
        .then(res => res.ok ? res.json() : [])
        .then(setBeerSuggestions)
    }
  }

  const selectBeer = (index: number, beer: BeerSuggestion) =>
    setEditForm(form => form && ({
      ...form,
      beers: form.beers.map((b, i) => i === index
        ? { ...b, beerId: beer._id, name: beer.name, brewery: beer.brewery ?? '', url: beer.url ?? '' }
        : b
      ),
    }))

  const cancelEdit = () => { setIsEditing(false); setEditForm(null); setPendingParticipantId(''); setPendingParticipantName('') }

  const confirmAddParticipant = () => {
    if (!pendingParticipantId) return
    if (pendingParticipantId === '__other__') {
      if (!pendingParticipantName.trim()) return
      setEditForm(form => form && ({
        ...form,
        participants: [...form.participants, { name: pendingParticipantName.trim(), isGuest: false, isOther: true }],
      }))
    } else {
      const member = members.find(member => member._id === pendingParticipantId)
      if (!member) return
      setEditForm(form => form && ({
        ...form,
        participants: [...form.participants, { name: member.name ?? member.email, memberId: member._id, isGuest: false }],
      }))
    }
    setPendingParticipantId('')
    setPendingParticipantName('')
  }

  const handleSave = async () => {
    if (!editForm) return
    setSaving(true)
    const body = {
      date: editForm.date,
      time: editForm.time.trim(),
      location: editForm.location.trim(),
      hosts: editForm.hosts.filter(host => host.name.trim()).map(host => ({ name: host.name.trim() })),
      theme: editForm.theme.trim() || null,
      food: editForm.food.trim() || null,
      notes: editForm.notes.trim() || null,
      beers: editForm.beers
        .filter(beer => beer.name.trim())
        .map(beer => ({
          beerId: beer.beerId,
          name: beer.name.trim(),
          brewery: beer.brewery.trim() || undefined,
          url: beer.url.trim() || undefined,
          foodPairing: beer.foodPairing.trim() || undefined,
        })),
      participants: editForm.participants
        .filter(participant => participant.name.trim())
        .map(participant => ({
          name: participant.name.trim(),
          memberId: participant.memberId,
          isGuest: participant.isGuest,
        })),
      participantCount: editForm.participants.filter(participant => participant.name.trim()).length,
    }
    const res = await apiFetch(`/api/meetings/${meeting._id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
    if (res.ok) {
      const updated = await res.json()
      onUpdate?.(updated)
      setIsEditing(false)
      setEditForm(null)
    }
    setSaving(false)
  }

  const addBeer = () => setEditForm(form => form && ({ ...form, beers: [...form.beers, { name: '', brewery: '', url: '', foodPairing: '' }] }))
  const removeBeer = (index: number) => setEditForm(form => form && ({ ...form, beers: form.beers.filter((_beer, itemIndex) => itemIndex !== index) }))
  const updateBeer = (index: number, field: keyof EditForm['beers'][0], value: string) =>
    setEditForm(form => form && ({ ...form, beers: form.beers.map((beer, itemIndex) => itemIndex === index ? { ...beer, [field]: value } : beer) }))

  const removeParticipant = (index: number) => setEditForm(form => form && ({ ...form, participants: form.participants.filter((_participant, itemIndex) => itemIndex !== index) }))

  const setParticipantMember = (index: number, memberId: string) => {
    const member = members.find(member => member._id === memberId)
    if (!member) return
    setEditForm(form => form && ({
      ...form,
      participants: form.participants.map((participant, itemIndex) =>
        itemIndex === index ? { name: member.name ?? member.email, memberId: member._id, isGuest: false } : participant
      ),
    }))
  }

  const setParticipantOther = (index: number) => {
    setEditForm(form => form && ({
      ...form,
      participants: form.participants.map((participant, itemIndex) =>
        itemIndex === index ? { name: '', memberId: undefined, isGuest: false, isOther: true } : participant
      ),
    }))
  }

  const updateParticipantName = (index: number, value: string) =>
    setEditForm(form => form && ({ ...form, participants: form.participants.map((participant, itemIndex) => itemIndex === index ? { ...participant, name: value } : participant) }))

  const toggleParticipantGuest = (index: number) =>
    setEditForm(form => form && ({ ...form, participants: form.participants.map((participant, itemIndex) => itemIndex === index ? { ...participant, isGuest: !participant.isGuest } : participant) }))

  const addHost = () => setEditForm(form => form && ({ ...form, hosts: [...form.hosts, { name: '', isOther: true }] }))
  const removeHost = (index: number) => setEditForm(form => form && ({ ...form, hosts: form.hosts.filter((_host, itemIndex) => itemIndex !== index) }))
  const updateHostName = (index: number, value: string) =>
    setEditForm(form => form && ({ ...form, hosts: form.hosts.map((host, itemIndex) => itemIndex === index ? { ...host, name: value } : host) }))
  const setHostMember = (index: number, memberId: string) => {
    const member = members.find(member => member._id === memberId)
    if (!member) return
    setEditForm(form => form && ({ ...form, hosts: form.hosts.map((host, itemIndex) => itemIndex === index ? { name: member.name ?? member.email, memberId: member._id, isOther: false } : host) }))
  }
  const setHostOther = (index: number) =>
    setEditForm(form => form && ({ ...form, hosts: form.hosts.map((host, itemIndex) => itemIndex === index ? { name: '', memberId: undefined, isOther: true } : host) }))

  return (
    <>
      <Box
        ref={scrollRef}
        {...glassCard}
        borderColor={isNext ? 'orange.300' : 'whiteAlpha.200'}
        borderRadius="lg"
        overflow="hidden"
        transition="all 0.2s"
      >
        {/* Card header */}
        <Box
          p={{ base: 3, md: 5 }}
          cursor="pointer"
          _hover={{ bg: 'whiteAlpha.100' }}
          onClick={() => {
            if (isEditing) { cancelEdit(); setIsExpanded(false) }
            else setIsExpanded(current => !current)
          }}
          position="relative"
        >
          {isNext && (
            <Badge position="absolute" top={3} right={onDelete ? 10 : 3} colorScheme="orange" fontSize="xs">Next Meeting</Badge>
          )}
          {onDelete && isAdmin && (
            <IconButton
              aria-label="Delete meeting"
              icon={<DeleteIcon />}
              size="xs"
              position="absolute"
              top={3}
              right={3}
              variant="ghost"
              color="red.300"
              _hover={{ bg: 'whiteAlpha.200', color: 'red.200' }}
              onClick={event => { event.stopPropagation(); onDeleteOpen() }}
            />
          )}
          <VStack align="start" spacing={3}>
            <VStack align="start" spacing={1}>
              <HStack spacing={2} flexWrap="wrap">
                <Text color="white" fontWeight="semibold" fontSize={{ base: 'sm', md: 'lg' }}>{display} • {dayOfWeek}</Text>
                {meeting.isAnnualMeeting && <Badge colorScheme="purple" fontSize="xs">Årsmöte</Badge>}
              </HStack>
              <Text color="whiteAlpha.700" fontSize="sm">🕕 {meeting.time}</Text>
              <Text color="whiteAlpha.700" fontSize="sm">📍 {meeting.location}</Text>
              {meeting.theme && <Text color="orange.300" fontSize="sm">🎯 {meeting.theme}</Text>}
            </VStack>
            <Divider borderColor="whiteAlpha.200" />
            <HStack spacing={3} w="full">
              {meeting.hosts.length > 0 ? (
                <>
                  <AvatarGroup size="sm" max={3}>
                    {meeting.hosts.map((host, index) => <Avatar key={index} name={host.name} border="2px solid" borderColor="whiteAlpha.300" />)}
                  </AvatarGroup>
                  <VStack align="start" spacing={0}>
                    <Text color="white" fontSize="sm">{meeting.hosts.map(host => host.name).join(' & ')}</Text>
                    <Text color="whiteAlpha.600" fontSize="xs">
                      {isPast ? `🍺 ${meeting.beers.length} beers tasted` : '🤫 Secret beer selection'}
                    </Text>
                  </VStack>
                </>
              ) : (
                <Text color="whiteAlpha.500" fontSize="sm" fontStyle="italic">No hosts recorded</Text>
              )}
              <HStack ml="auto" spacing={2}>
                {currentUserId && !isAlreadyAttending && (
                  <Button
                    size="xs"
                    variant="ghost"
                    color="green.300"
                    _hover={{ bg: 'whiteAlpha.200' }}
                    isLoading={attending}
                    onClick={event => { event.stopPropagation(); handleAttend() }}
                  >
                    {isPast ? 'Attended' : 'Attend'}
                  </Button>
                )}
                {meeting.participantCount > 0 && (
                  <Badge colorScheme="blue" borderRadius="full">{meeting.participantCount} {isPast ? 'attended' : 'attending'}</Badge>
                )}
                {isAdmin && (
                  <IconButton
                    aria-label="Edit meeting"
                    icon={<EditIcon />}
                    size="xs"
                    variant="ghost"
                    color="whiteAlpha.500"
                    _hover={{ color: 'white' }}
                    onClick={event => { event.stopPropagation(); startEdit() }}
                  />
                )}
                <IconButton
                  aria-label={isExpanded ? 'Collapse' : 'Expand'}
                  icon={isExpanded ? <ChevronUpIcon /> : <ChevronDownIcon />}
                  size="xs"
                  variant="ghost"
                  color="whiteAlpha.600"
                  _hover={{ color: 'white' }}
                  onClick={event => { event.stopPropagation(); if (isEditing) { cancelEdit(); setIsExpanded(false) } else setIsExpanded(current => !current) }}
                />
              </HStack>
            </HStack>
          </VStack>
        </Box>

        {/* Expandable section */}
        <Collapse in={isExpanded} animateOpacity>
          <Box borderTop="1px solid" borderColor="whiteAlpha.100">

            {isEditing && editForm ? (
              <Box px={{ base: 3, md: 5 }} py={5}>
                <VStack align="stretch" spacing={5}>

                  <HStack justify="flex-end" spacing={3}>
                    <Button size="sm" variant="ghost" color="whiteAlpha.700" onClick={cancelEdit}>Cancel</Button>
                    <Button size="sm" colorScheme="orange" isLoading={saving} onClick={handleSave}>Save Changes</Button>
                  </HStack>

                  <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={4}>
                    <FormControl>
                      <FormLabel {...labelStyle}>Date</FormLabel>
                      <Input
                        type="date"
                        value={editForm.date}
                        onChange={event => setEditForm(form => form && ({ ...form, date: event.target.value }))}
                        {...inputStyle}
                        sx={{ '::-webkit-calendar-picker-indicator': { filter: 'invert(1)' } }}
                      />
                    </FormControl>
                    <FormControl>
                      <FormLabel {...labelStyle}>Time</FormLabel>
                      <Input
                        placeholder="e.g. 18:00"
                        value={editForm.time}
                        onChange={event => setEditForm(form => form && ({ ...form, time: event.target.value }))}
                        {...inputStyle}
                      />
                    </FormControl>
                    <FormControl gridColumn={{ sm: 'span 2' }}>
                      <FormLabel {...labelStyle}>Location</FormLabel>
                      <Input
                        placeholder="e.g. Storgatan 1"
                        value={editForm.location}
                        onChange={event => setEditForm(form => form && ({ ...form, location: event.target.value }))}
                        {...inputStyle}
                      />
                    </FormControl>
                  </SimpleGrid>

                  <Divider borderColor="whiteAlpha.100" />

                  <VStack align="stretch" spacing={2}>
                    <HStack justify="space-between">
                      <Text {...labelStyle}>Hosts</Text>
                      <Button size="xs" variant="ghost" color="orange.300" leftIcon={<AddIcon />} onClick={addHost}>Add</Button>
                    </HStack>
                    {editForm.hosts.map((host, index) => (
                      <VStack key={index} align="stretch" spacing={1}>
                        <HStack spacing={2}>
                          <Select
                            size="sm"
                            value={host.memberId ?? (host.isOther ? '__other__' : '')}
                            onChange={event => {
                              if (event.target.value === '__other__') setHostOther(index)
                              else if (event.target.value) setHostMember(index, event.target.value)
                            }}
                            color="white"
                            borderColor="whiteAlpha.300"
                            bg="transparent"
                            sx={{ option: { background: '#2D3748' } }}
                            flex={1}
                          >
                            {members
                              .filter(member => member.name || member.email)
                              .sort((memberA, memberB) => (memberA.name ?? memberA.email).localeCompare(memberB.name ?? memberB.email))
                              .map(member => (
                                <option key={member._id} value={member._id}>{member.name ?? member.email}</option>
                              ))}
                            <option value="__other__">Other (enter name)</option>
                          </Select>
                          <IconButton aria-label="Remove" icon={<DeleteIcon />} size="xs" variant="ghost" color="red.300" onClick={() => removeHost(index)} />
                        </HStack>
                        {host.isOther && (
                          <Input
                            placeholder="Host name"
                            value={host.name}
                            onChange={event => updateHostName(index, event.target.value)}
                            {...inputStyle}
                          />
                        )}
                      </VStack>
                    ))}
                  </VStack>

                  <Divider borderColor="whiteAlpha.100" />

                  <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={4}>
                    <FormControl>
                      <FormLabel {...labelStyle}>Theme</FormLabel>
                      <Input placeholder="e.g. Blindprovning" value={editForm.theme} onChange={event => setEditForm(form => form && ({ ...form, theme: event.target.value }))} {...inputStyle} />
                    </FormControl>
                    <FormControl>
                      <FormLabel {...labelStyle}>Food</FormLabel>
                      <Input placeholder="e.g. Pizza" value={editForm.food} onChange={event => setEditForm(form => form && ({ ...form, food: event.target.value }))} {...inputStyle} />
                    </FormControl>
                  </SimpleGrid>

                  <Divider borderColor="whiteAlpha.100" />

                  <VStack align="stretch" spacing={2}>
                    <HStack justify="space-between">
                      <Text {...labelStyle}>Beers Tasted</Text>
                      <Button size="xs" variant="ghost" color="orange.300" leftIcon={<AddIcon />} onClick={addBeer}>Add Beer</Button>
                    </HStack>
                    {editForm.beers.map((beer, index) => (
                      <Box key={index} bg="whiteAlpha.50" borderRadius="md" p={3}>
                        <HStack spacing={2} align="flex-start">
                          <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={2} flex={1}>
                            <BreweryInput value={beer.brewery} onChange={v => updateBeer(index, 'brewery', v)} suggestions={beerSuggestions} />
                            <BeerNameInput value={beer.name} onChange={v => updateBeer(index, 'name', v)} onSelect={b => selectBeer(index, b)} suggestions={beerSuggestions} breweryHint={beer.brewery} />
                            <Input placeholder="URL" value={beer.url} onChange={event => updateBeer(index, 'url', event.target.value)} {...inputStyle} />
                            <Input placeholder="Food pairing" value={beer.foodPairing} onChange={event => updateBeer(index, 'foodPairing', event.target.value)} {...inputStyle} />
                          </SimpleGrid>
                          <IconButton aria-label="Remove beer" icon={<DeleteIcon />} size="xs" variant="ghost" color="red.300" mt={1} onClick={() => removeBeer(index)} />
                        </HStack>
                      </Box>
                    ))}
                    {editForm.beers.length === 0 && (
                      <Text color="whiteAlpha.400" fontSize="sm" fontStyle="italic">No beers added yet</Text>
                    )}
                  </VStack>

                  <Divider borderColor="whiteAlpha.100" />

                  <VStack align="stretch" spacing={2}>
                    <Text {...labelStyle}>Attendees</Text>
                    <HStack spacing={2}>
                      <Select
                        size="sm"
                        value={pendingParticipantId}
                        placeholder="Add attendee…"
                        onChange={event => { setPendingParticipantId(event.target.value); setPendingParticipantName('') }}
                        color="white"
                        borderColor="whiteAlpha.300"
                        bg="transparent"
                        sx={{ option: { background: '#2D3748' } }}
                        flex={1}
                      >
                        {members
                          .filter(member => member.name || member.email)
                          .filter(member => !editForm.participants.some(participant => participant.memberId === member._id))
                          .sort((memberA, memberB) => (memberA.name ?? memberA.email).localeCompare(memberB.name ?? memberB.email))
                          .map(member => (
                            <option key={member._id} value={member._id}>{member.name ?? member.email}</option>
                          ))}
                        <option value="__other__">Other (enter name)</option>
                      </Select>
                      <Button
                        size="sm" colorScheme="orange" variant="ghost" leftIcon={<AddIcon />}
                        onClick={confirmAddParticipant}
                        isDisabled={!pendingParticipantId || (pendingParticipantId === '__other__' && !pendingParticipantName.trim())}
                      >
                        Add
                      </Button>
                    </HStack>
                    {pendingParticipantId === '__other__' && (
                      <Input
                        placeholder="Enter name"
                        value={pendingParticipantName}
                        onChange={event => setPendingParticipantName(event.target.value)}
                        onKeyDown={event => { if (event.key === 'Enter') confirmAddParticipant() }}
                        {...inputStyle}
                        autoFocus
                      />
                    )}
                    <Input
                      placeholder="Filter attendees…"
                      value={participantFilter}
                      onChange={event => setParticipantFilter(event.target.value)}
                      {...inputStyle}
                    />
                    <VStack spacing={2} align="stretch">
                      {editForm.participants.map((participant, index) => {
                        if (participantFilter && !participant.name.toLowerCase().includes(participantFilter.toLowerCase())) return null
                        return (
                        <VStack key={index} align="stretch" spacing={1}>
                          <HStack spacing={2}>
                            <Select
                              size="sm"
                              value={participant.memberId ?? (participant.isOther ? '__other__' : '')}
                              onChange={event => {
                                if (event.target.value === '__other__') setParticipantOther(index)
                                else if (event.target.value) setParticipantMember(index, event.target.value)
                              }}
                              color="white"
                              borderColor="whiteAlpha.300"
                              bg="transparent"
                              sx={{ option: { background: '#2D3748' } }}
                              flex={1}
                            >
                              {members
                                .filter(member => member.name || member.email)
                                .filter(member => !editForm.participants.some((participant, participantIndex) =>
                                  participant.memberId === member._id && participantIndex !== index
                                ))
                                .sort((memberA, memberB) => (memberA.name ?? memberA.email).localeCompare(memberB.name ?? memberB.email))
                                .map(member => (
                                  <option key={member._id} value={member._id}>{member.name ?? member.email}</option>
                                ))}
                              <option value="__other__">Other (enter name)</option>
                            </Select>
                            <IconButton aria-label="Remove" icon={<DeleteIcon />} size="xs" variant="ghost" color="red.300" onClick={() => removeParticipant(index)} />
                          </HStack>
                          {participant.isOther && (
                            <HStack spacing={2} pl={1}>
                              <Input
                                placeholder="Name"
                                value={participant.name}
                                onChange={event => updateParticipantName(index, event.target.value)}
                                {...inputStyle}
                                flex={1}
                              />
                              <Checkbox isChecked={participant.isGuest} onChange={() => toggleParticipantGuest(index)} colorScheme="orange" color="whiteAlpha.600" size="sm" whiteSpace="nowrap">
                                Guest
                              </Checkbox>
                            </HStack>
                          )}
                        </VStack>
                        )
                      })}
                    </VStack>
                  </VStack>

                  <Divider borderColor="whiteAlpha.100" />

                  <FormControl>
                    <FormLabel {...labelStyle}>Notes</FormLabel>
                    <Textarea
                      placeholder="Any notes about this meeting…"
                      value={editForm.notes}
                      onChange={event => setEditForm(form => form && ({ ...form, notes: event.target.value }))}
                      {...inputStyle}
                      rows={3}
                    />
                  </FormControl>

                  <HStack justify="flex-end" spacing={3}>
                    <Button size="sm" variant="ghost" color="whiteAlpha.700" onClick={cancelEdit}>Cancel</Button>
                    <Button size="sm" colorScheme="orange" isLoading={saving} onClick={handleSave}>Save Changes</Button>
                  </HStack>

                </VStack>
              </Box>

            ) : (
              <Box px={{ base: 3, md: 5 }} pb={5}>
                <VStack align="stretch" spacing={4} pt={4}>

                  {meeting.beers.length > 0 ? (
                    <VStack align="stretch" spacing={2}>
                      <Text {...labelStyle}>Beers Tasted</Text>
                      {meeting.beers.map((beer, index) => (
                        <HStack key={index} spacing={3} align="start">
                          <Text color="orange.300" fontSize="sm" minW="16px">{index + 1}.</Text>
                          <VStack align="start" spacing={0} flex={1}>
                            <HStack spacing={1} flexWrap="wrap">
                              {beer.brewery && <Text color="whiteAlpha.600" fontSize="sm">{beer.brewery} –</Text>}
                              {beer.url ? (
                                <Link href={beer.url} isExternal color="white" fontSize="sm" fontWeight="medium" textDecoration="underline" _hover={{ color: 'orange.300', textDecoration: 'none' }} onClick={event => event.stopPropagation()}>
                                  {beer.name}
                                </Link>
                              ) : (
                                <Text color="white" fontSize="sm" fontWeight="medium">{beer.name}</Text>
                              )}
                            </HStack>
                            {beer.foodPairing && <Text color="whiteAlpha.500" fontSize="xs">🍽 {beer.foodPairing}</Text>}
                          </VStack>
                        </HStack>
                      ))}
                    </VStack>
                  ) : isPast && !meeting.food && (meeting.participants.length > 0 || !!meeting.notes) && (
                    <VStack align="stretch" spacing={1}>
                      <Text {...labelStyle}>Drinks & Food</Text>
                      <Text color="whiteAlpha.400" fontSize="sm" fontStyle="italic">Nothing recorded.</Text>
                    </VStack>
                  )}

                  {meeting.food && (
                    <VStack align="stretch" spacing={1}>
                      <Text {...labelStyle}>Food</Text>
                      <Text color="whiteAlpha.800" fontSize="sm">🍽 {meeting.food}</Text>
                    </VStack>
                  )}

                  {meeting.participants.length > 0 && (
                    <VStack align="stretch" spacing={2}>
                      <HStack justify="space-between" align="center">
                        <Text {...labelStyle}>
                          Attendees ({meeting.participants.filter(participant => !participant.isGuest).length} members{meeting.participants.filter(participant => participant.isGuest).length > 0 ? ` + ${meeting.participants.filter(participant => participant.isGuest).length} guests` : ''})
                        </Text>
                        {meeting.participants.some(participant => participant.memberId) && (
                          <Button size="xs" variant="ghost" color="whiteAlpha.500" _hover={{ color: 'white' }} onClick={handleDietaryOpen}>
                            Dietary
                          </Button>
                        )}
                      </HStack>
                      <HStack flexWrap="wrap" spacing={2} rowGap={1}>
                        {[...meeting.participants].sort((participantA, participantB) => participantA.name.localeCompare(participantB.name)).map((participant, index) =>
                          participant.isGuest ? (
                            <Badge key={index} colorScheme="gray" variant="subtle" fontSize="xs">{participant.name} (gäst)</Badge>
                          ) : participant.memberId ? (
                            <Link key={index} as={RouterLink} to={`/members/${participant.memberId}`} color="whiteAlpha.700" fontSize="sm" textDecoration="underline" _hover={{ color: 'white' }} onClick={event => event.stopPropagation()}>
                              {participant.name}
                            </Link>
                          ) : (
                            <Text key={index} color="whiteAlpha.700" fontSize="sm">{participant.name}</Text>
                          )
                        )}
                      </HStack>
                    </VStack>
                  )}

                  {meeting.notes && (
                    <VStack align="stretch" spacing={1}>
                      <Text {...labelStyle}>Notes</Text>
                      <Text color="whiteAlpha.700" fontSize="sm" whiteSpace="pre-wrap">{meeting.notes}</Text>
                    </VStack>
                  )}

                  {!meeting.beers.length && !meeting.food && !meeting.participants.length && !meeting.notes && (
                    <Text color="whiteAlpha.400" fontSize="sm" fontStyle="italic">
                      {isAdmin ? 'No details yet. Click Edit to add beers, food and attendees.' : 'Nothing has been recorded for this meeting yet.'}
                    </Text>
                  )}

                </VStack>
              </Box>
            )}
          </Box>
        </Collapse>
      </Box>

      <Modal isOpen={isDietaryOpen} onClose={onDietaryClose} size="sm" isCentered>
        <ModalOverlay />
        <ModalContent bg="gray.800" border="1px solid" borderColor="whiteAlpha.200">
          <ModalHeader color="white" fontSize="md">Dietary Restrictions</ModalHeader>
          <ModalCloseButton color="whiteAlpha.600" />
          <ModalBody pb={6}>
            {dietaryLoading ? (
              <Spinner color="white" size="sm" />
            ) : dietaryInfo.length === 0 ? (
              <Text color="whiteAlpha.600" fontSize="sm">No dietary restrictions registered for linked members.</Text>
            ) : (
              <VStack align="stretch" spacing={3}>
                {dietaryInfo.map((dietaryEntry, index) => (
                  <VStack key={index} align="start" spacing={0}>
                    <Text color="white" fontSize="sm" fontWeight="semibold">{dietaryEntry.name}</Text>
                    <Text color="whiteAlpha.700" fontSize="sm">{dietaryEntry.dietary}</Text>
                  </VStack>
                ))}
              </VStack>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>

      <AlertDialog isOpen={isDeleteOpen} leastDestructiveRef={cancelDeleteRef} onClose={onDeleteClose}>
        <AlertDialogOverlay>
          <AlertDialogContent bg="gray.800" border="1px solid" borderColor="whiteAlpha.200">
            <AlertDialogHeader fontSize="lg" fontWeight="bold" color="white">Delete Meeting</AlertDialogHeader>
            <AlertDialogBody color="whiteAlpha.800">
              Delete the meeting on {display}? This cannot be undone.
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelDeleteRef} variant="ghost" color="whiteAlpha.700" onClick={onDeleteClose}>Cancel</Button>
              <Button colorScheme="red" onClick={() => { onDelete?.(meeting._id); onDeleteClose() }} ml={3}>Delete</Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  )
}
